import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { HexColorPicker } from 'react-colorful';

const DepartmentSettings = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptColor, setNewDeptColor] = useState('#3B82F6');
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  useEffect(() => {
    fetchDepartments();
  }, []);
  
  const fetchDepartments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/departments');
      
      if (response.data && response.data.departments) {
        setDepartments(response.data.departments);
      } else {
        setDepartments([]);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError('Failed to load departments. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddDepartment = async (e) => {
    e.preventDefault();
    
    if (!newDeptName.trim()) {
      toast.error('Department name cannot be empty');
      return;
    }
    
    try {
      const response = await api.post('/departments', {
        name: newDeptName,
        color: newDeptColor
      });
      
      if (response.data && response.data.department) {
        setDepartments([...departments, response.data.department]);
        
        // Reset form
        setNewDeptName('');
        setNewDeptColor('#3B82F6');
        setShowAddForm(false);
        
        toast.success('Department added successfully');
      }
    } catch (error) {
      console.error('Error adding department:', error);
      toast.error(error.response?.data?.error || 'Failed to add department');
    }
  };
  
  const handleEditDepartment = async (e) => {
    e.preventDefault();
    
    if (!newDeptName.trim()) {
      toast.error('Department name cannot be empty');
      return;
    }
    
    try {
      const response = await api.put(`/departments/${editingDepartment.id}`, {
        name: newDeptName,
        color: newDeptColor
      });
      
      if (response.data && response.data.department) {
        // Update departments list
        setDepartments(
          departments.map(dept => 
            dept.id === editingDepartment.id ? response.data.department : dept
          )
        );
        
        // Reset form
        setEditingDepartment(null);
        setNewDeptName('');
        setNewDeptColor('#3B82F6');
        setShowEditForm(false);
        
        toast.success('Department updated successfully');
      }
    } catch (error) {
      console.error('Error updating department:', error);
      toast.error(error.response?.data?.error || 'Failed to update department');
    }
  };
  
  const handleDeleteDepartment = async (departmentId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this department? Projects in this department will be moved to the Default department.');
    
    if (!confirmDelete) return;
    
    try {
      await api.delete(`/departments/${departmentId}`);
      
      // Remove from departments list
      setDepartments(departments.filter(dept => dept.id !== departmentId));
      
      toast.success('Department deleted successfully');
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error(error.response?.data?.error || 'Failed to delete department');
    }
  };
  
  const handleEditClick = (department) => {
    setEditingDepartment(department);
    setNewDeptName(department.name);
    setNewDeptColor(department.color);
    setShowEditForm(true);
    setShowAddForm(false);
  };
  
  const handleAddClick = () => {
    setShowAddForm(true);
    setShowEditForm(false);
    setNewDeptName('');
    setNewDeptColor('#3B82F6');
  };
  
  if (loading && departments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48">
        <div className="w-10 h-10 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading departments...</p>
      </div>
    );
  }
  
  if (error && departments.length === 0) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <p>{error}</p>
        <button 
          onClick={fetchDepartments}
          className="mt-2 text-red-700 underline"
        >
          Retry
        </button>
      </div>
    );
  }
  
  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Departments</h2>
        <button
          type="button"
          onClick={handleAddClick}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Department
        </button>
      </div>
      
      {/* Add/Edit Form */}
      {(showAddForm || showEditForm) && (
        <div className="bg-gray-50 p-4 rounded-md mb-6 border border-gray-200">
          <h3 className="text-base font-medium text-gray-900 mb-3">
            {showAddForm ? 'Add New Department' : 'Edit Department'}
          </h3>
          <form onSubmit={showAddForm ? handleAddDepartment : handleEditDepartment}>
            <div className="flex space-x-4">
              <div className="flex-grow">
                <label htmlFor="departmentName" className="block text-sm font-medium text-gray-700 mb-1">
                  Department Name
                </label>
                <input
                  type="text"
                  id="departmentName"
                  value={newDeptName}
                  onChange={(e) => setNewDeptName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2"
                  placeholder="Enter department name"
                  disabled={editingDepartment?.name === 'Default'}
                />
                {editingDepartment?.name === 'Default' && (
                  <p className="text-xs text-orange-600 mt-1">
                    The Default department name cannot be changed.
                  </p>
                )}
              </div>
              <div className="w-48">
                <label htmlFor="departmentColor" className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="relative">
                  <div 
                    className="w-full h-10 rounded-md border border-gray-300 cursor-pointer"
                    style={{ backgroundColor: newDeptColor }}
                    onClick={() => setShowColorPicker(!showColorPicker)}
                  ></div>
                  
                  {showColorPicker && (
                    <div className="absolute z-10 mt-1">
                      <HexColorPicker 
                        color={newDeptColor} 
                        onChange={setNewDeptColor}
                      />
                      <div className="flex mt-2 justify-end">
                        <button
                          type="button"
                          onClick={() => setShowColorPicker(false)}
                          className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-4 space-x-3">
              <button
                type="button"
                onClick={() => showAddForm ? setShowAddForm(false) : setShowEditForm(false)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-2 bg-blue-600 rounded-md text-sm text-white hover:bg-blue-700"
                disabled={editingDepartment?.name === 'Default' && newDeptName === 'Default'}
              >
                {showAddForm ? 'Add Department' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Departments List */}
      {departments.length > 0 ? (
        <div className="overflow-hidden bg-white shadow sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {departments.map((department) => (
              <li key={department.id}>
                <div className="flex items-center px-4 py-4 sm:px-6">
                  <div className="flex items-center flex-1 min-w-0">
                    <div 
                      className="flex-shrink-0 w-6 h-6 rounded"
                      style={{ backgroundColor: department.color }}
                    ></div>
                    <div className="px-4 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {department.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        Created: {new Date(department.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => handleEditClick(department)}
                      className="ml-2 inline-flex items-center p-1.5 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {department.name !== 'Default' && (
                      <button
                        onClick={() => handleDeleteDepartment(department.id)}
                        className="ml-2 inline-flex items-center p-1.5 text-gray-500 hover:text-red-600 rounded-full hover:bg-gray-100"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-md">
          <p className="text-gray-500">No departments have been created yet.</p>
        </div>
      )}
    </div>
  );
};

export default DepartmentSettings; 